import { Principal } from "@icp-sdk/core/principal";
import { ROLE_MEMBER } from "../constants";
import { parseBigInt, random64 } from "../utils";
import { ValidationErrors } from "../utils/validation";
import { chatIdentifiersEqual, } from "./chat";
import { communityIdentifiersEqual, } from "./community";
export const MIN_BOT_NAME_LENGTH = 5;
export const MIN_COMMAND_NAME_LENGTH = 3;
export const MIN_PARAM_NAME_LENGTH = 1;
function defaultCommonParam(param) {
    return {
        name: param?.name ?? "",
        description: param?.description ?? "",
        placeholder: param?.placeholder ?? "",
        required: param?.required ?? true,
        visible_when: param?.visible_when,
    };
}
export function defaultBooleanParam(param) {
    return {
        kind: "boolean",
        ...defaultCommonParam(param),
    };
}
export function defaultStringParam(param) {
    return {
        kind: "string",
        ...defaultCommonParam(param),
        minLength: 0,
        maxLength: 1000,
        choices: [],
        multi_line: false,
    };
}
export function defaultIntegerParam(param) {
    return {
        kind: "integer",
        ...defaultCommonParam(param),
        minValue: BigInt(0),
        maxValue: BigInt(1000),
        choices: [],
    };
}
export function defaultDecimalParam(param) {
    return {
        kind: "decimal",
        ...defaultCommonParam(param),
        minValue: 0,
        maxValue: 1000,
        choices: [],
    };
}
export function defaultUserParam(param) {
    return {
        kind: "user",
        ...defaultCommonParam(param),
    };
}
export function defaultDateTimeParam(param) {
    return {
        kind: "dateTime",
        ...defaultCommonParam(param),
        future_only: false,
    };
}
export function emptyBotCommand() {
    return {
        name: "",
        description: "",
        params: [],
        defaultRole: ROLE_MEMBER,
        permissions: emptyExternalBotPermissions(),
        directMessages: false,
    };
}
export function emptyExternalBotPermissions() {
    return {
        chatPermissions: [],
        communityPermissions: [],
        messagePermissions: [],
    };
}
export function emptyGrantedBotPermissions() {
    return {
        command: {
            chatPermissions: [],
            communityPermissions: [],
            messagePermissions: [],
        },
        autonomous: undefined,
    };
}
export function flattenCommandPermissions(definition) {
    return definition.commands.reduce((p, c) => {
        return mergePermissions(p, c.permissions);
    }, emptyExternalBotPermissions());
}
export function definitionToPermissions(definition) {
    return {
        command: flattenCommandPermissions(definition),
        autonomous: definition.autonomousConfig
            ? { ...definition.autonomousConfig.permissions }
            : undefined,
    };
}
/**
 * The permissions to offer the user to grant a bot being installed: what it asks for, without any
 * community permissions when it isn't being installed in a community, where they mean nothing
 */
export function permissionsToGrant(definition, location) {
    const permissions = definitionToPermissions(definition);
    if (location.kind === "community")
        return permissions;
    return {
        command: { ...permissions.command, communityPermissions: [] },
        autonomous: permissions.autonomous === undefined
            ? undefined
            : { ...permissions.autonomous, communityPermissions: [] },
    };
}
function mergeLists(l1, l2) {
    return [...new Set([...l1, ...l2])];
}
function mergePermissions(p1, p2) {
    return {
        chatPermissions: mergeLists(p1.chatPermissions, p2.chatPermissions),
        communityPermissions: mergeLists(p1.communityPermissions, p2.communityPermissions),
        messagePermissions: mergeLists(p1.messagePermissions, p2.messagePermissions),
    };
}
export function setifyCommandPermissions(perm) {
    return {
        chatPermissions: new Set(perm.chatPermissions),
        communityPermissions: new Set(perm.communityPermissions),
        messagePermissions: new Set(perm.messagePermissions),
    };
}
function hasEveryPermissionOfType(required, granted, type) {
    const r = required[type];
    const g = granted[type];
    return r.every((p) => g.has(p));
}
export function hasEveryRequiredPermission(required, granted) {
    const grantedSet = setifyCommandPermissions(granted);
    return (hasEveryPermissionOfType(required, grantedSet, "chatPermissions") &&
        hasEveryPermissionOfType(required, grantedSet, "communityPermissions") &&
        hasEveryPermissionOfType(required, grantedSet, "messagePermissions"));
}
export function emptyBotInstance(ownerId) {
    return {
        kind: "external_bot",
        id: "",
        ownerId,
        name: "",
        endpoint: "",
        definition: {
            kind: "bot_definition",
            description: "",
            commands: [],
            autonomousConfig: undefined,
            defaultSubscriptions: undefined,
            dataEncoding: undefined,
            restrictedLocations: undefined,
            miniApps: [],
        },
        registrationStatus: { kind: "private" },
    };
}
function singleStringParam(params) {
    return params.length === 1 && params[0].kind === "string";
}
/**
 * The params a command's form shows given its args, each with the choices it shows, or undefined
 * for a param it hides. A param or a choice with a condition is shown while the earlier param the
 * condition names is shown, with one of the condition's values among its shown choices: eg. a
 * game's settings once that game is chosen. A param with choices, none of them shown, is hidden.
 * Each param's condition only looks at earlier params, so `args` can stop short, at the param
 * whose turn it is.
 */
export function shownParams(params, args) {
    const shown = [];
    const holds = (condition) => {
        if (condition === undefined)
            return true;
        const index = params.findIndex((p) => p.name === condition.param);
        // undefined for a param which isn't earlier, as well as for one which is hidden
        const param = shown[index];
        const arg = args[index];
        return (param?.kind === "string" &&
            arg?.kind === "string" &&
            arg.value !== undefined &&
            condition.values.includes(arg.value) &&
            param.choices.some((c) => c.value === arg.value));
    };
    for (const param of params) {
        shown.push(holds(param.visible_when) ? withShownChoices(param, holds) : undefined);
    }
    return shown;
}
// The param with only the choices whose conditions hold, or undefined when none of them does
function withShownChoices(param, holds) {
    switch (param.kind) {
        case "string": {
            const choices = shownChoices(param.choices, holds);
            return choices === param.choices ? param : choices && { ...param, choices };
        }
        case "integer": {
            const choices = shownChoices(param.choices, holds);
            return choices === param.choices ? param : choices && { ...param, choices };
        }
        case "decimal": {
            const choices = shownChoices(param.choices, holds);
            return choices === param.choices ? param : choices && { ...param, choices };
        }
        default:
            return param;
    }
}
// The same list when no choice has a condition, so the form keeps what it has
function shownChoices(choices, holds) {
    if (!choices.some((c) => c.visible_when !== undefined))
        return choices;
    const shown = choices.filter((c) => holds(c.visible_when));
    return shown.length > 0 ? shown : undefined;
}
/**
 * The arg as its shown param takes it: unset when its value is a choice the param doesn't show,
 * eg. a time limit only one game has, once another game is chosen. The arg itself keeps the
 * value, for when the choice is shown again.
 */
function unsetIfHiddenChoice(param, arg) {
    if (param.kind === "string" && arg.kind === "string") {
        // "" is how the form has a choice not yet made
        const hidden = param.choices.length > 0 &&
            !!arg.value &&
            !param.choices.some((c) => c.value === arg.value);
        return hidden ? { ...arg, value: undefined } : arg;
    }
    if ((param.kind === "integer" && arg.kind === "integer") ||
        (param.kind === "decimal" && arg.kind === "decimal")) {
        const choices = param.choices;
        const hidden = choices.length > 0 && arg.value !== null && !choices.some((c) => c.value === arg.value);
        return hidden ? { ...arg, value: null } : arg;
    }
    return arg;
}
/** Whether the args are valid for the params the form shows, against the choices it shows */
export function shownArgsAreValid(params, args) {
    const shown = shownParams(params, args);
    return shown.every((p, i) => p === undefined || argIsValid(p, unsetIfHiddenChoice(p, args[i])));
}
/**
 * The args to send: those of the params the form shows, with a value that's a choice its param
 * doesn't show unset. A hidden param's arg isn't sent.
 */
export function shownArgs(params, args) {
    const shown = shownParams(params, args);
    return args.flatMap((arg, i) => {
        const param = shown[i];
        return param === undefined ? [] : [unsetIfHiddenChoice(param, arg)];
    });
}
// A param's condition and its choices' conditions
function conditionsOf(param) {
    const choices = param.kind === "string" || param.kind === "integer" || param.kind === "decimal"
        ? param.choices
        : [];
    return [param.visible_when, ...choices.map((c) => c.visible_when)].filter((c) => c !== undefined);
}
export function createArgsFromSchema(params, maybeArgs) {
    if (singleStringParam(params)) {
        return [{ kind: "string", name: params[0].name, value: maybeArgs.join(" ") }];
    }
    // The typed values go to the params shown, in order: a param which the values typed before it
    // hide takes none. Which params are shown hangs on the values of the params conditions name,
    // so once one of those isn't one of its choices, the rest are left for the form to ask for.
    const named = new Set(params.flatMap(conditionsOf).map((c) => c.param));
    const args = [];
    let typed = 0;
    let stopped = false;
    for (const p of params) {
        const shown = shownParams(params, args)[args.length];
        const value = shown !== undefined && !stopped ? maybeArgs[typed++] : undefined;
        const arg = createArg(shown ?? p, value);
        if (value !== undefined && named.has(p.name) && arg.kind === "string" && !arg.value) {
            stopped = true;
        }
        args.push(arg);
    }
    return args;
}
function createArg(p, typed) {
    switch (p.kind) {
        case "user":
            return { kind: "user", name: p.name };
        case "boolean": {
            const boolVal = (typed ?? "false").toLocaleLowerCase() === "true";
            return { kind: "boolean", name: p.name, value: boolVal };
        }
        case "integer": {
            let value = parseBigInt(typed ?? null) ?? null;
            if (p.choices.length > 0) {
                value = p.choices.find((c) => c.value === value)?.value ?? null;
            }
            return { kind: p.kind, name: p.name, value };
        }
        case "decimal": {
            const numParam = Number(typed);
            let value = isNaN(numParam) ? null : numParam;
            if (p.choices.length > 0) {
                value = p.choices.find((c) => c.value === value)?.value ?? null;
            }
            return { kind: p.kind, name: p.name, value };
        }
        case "string": {
            let strParam = typed ?? "";
            if (p.choices.length > 0) {
                strParam =
                    p.choices.find((c) => c.name.toLocaleLowerCase() === strParam.toLocaleLowerCase() ||
                        c.value.toLocaleLowerCase() === strParam.toLocaleLowerCase())?.value ?? "";
            }
            return { kind: "string", name: p.name, value: strParam };
        }
        case "dateTime": {
            return {
                name: p.name,
                kind: "dateTime",
                value: typed === undefined ? null : (parseBigInt(typed) ?? parseDateTime(typed)),
            };
        }
    }
}
// chrono-node reads a date typed in words ("tomorrow at 9") as the argument of a bot command.
// Only bot commands need it, so it is fetched once the user starts typing one rather than with
// the app. Until it has arrived a date in words is not understood, like any other text which is
// not a date, so whoever builds a command's arguments from typed text waits for this first.
let parseDate;
let loadingDateParser;
// Never rejects: without the parser a command can still be given its date in the command builder
export function loadDateParser() {
    // naming the one export used lets the bundler leave the rest of chrono-node out
    loadingDateParser ??= import("chrono-node").then(({ parseDate: loaded }) => {
        parseDate = loaded;
    }, (err) => {
        // allow a later command to retry (e.g. transient network failure)
        loadingDateParser = undefined;
        console.error("Failed to load the date parser", err);
    });
    return loadingDateParser;
}
function parseDateTime(value) {
    if (parseDate === undefined) {
        return null;
    }
    const now = new Date();
    const date = parseDate(value, { instant: now, timezone: -now.getTimezoneOffset() }, { forwardDate: true });
    if (date == null) {
        return null;
    }
    return BigInt(date.getTime());
}
export function argIsValid(schema, arg) {
    if (schema.kind === "user" && arg.kind === "user") {
        return !schema.required || arg.userId !== undefined;
    }
    else if (schema.kind === "boolean" && arg.kind === "boolean") {
        return !schema.required || arg.value !== undefined;
    }
    else if (schema.kind === "string" && arg.kind === "string") {
        if (!schema.required && arg.value === undefined)
            return true;
        if (arg.value === undefined)
            return false;
        if (schema.choices.length > 0) {
            return schema.choices.map((c) => c.value).includes(arg.value);
        }
        else {
            return arg.value.length >= schema.minLength && arg.value.length <= schema.maxLength;
        }
    }
    else if ((schema.kind === "integer" && arg.kind === "integer") ||
        (schema.kind === "decimal" && arg.kind === "decimal")) {
        if (!schema.required && arg.value === null)
            return true;
        if (arg.value === null)
            return false;
        if (schema.choices.length > 0) {
            return schema.choices.map((c) => c.value).includes(arg.value);
        }
        else {
            return arg.value >= schema.minValue && arg.value <= schema.maxValue;
        }
    }
    else if (schema.kind === "dateTime" && arg.kind === "dateTime") {
        return !schema.required || (arg.value !== undefined && arg.value !== null);
    }
    return false;
}
export function i18nKey(key, params, level, lowercase = false) {
    return {
        kind: "resource_key",
        key,
        params,
        level,
        lowercase,
    };
}
// This is used for bot and command names
export function validBotComponentName(name, min) {
    const errors = [];
    if (name.length < min) {
        errors.push(i18nKey("bots.builder.errors.minLength", { n: min }));
    }
    const regex = /^[a-zA-Z0-9_]+$/;
    if (!regex.test(name)) {
        errors.push(i18nKey("bots.builder.errors.alphaOnly"));
    }
    return errors;
}
export function validBotParamName(name, min) {
    const errors = [];
    if (name.length < min) {
        errors.push(i18nKey("bots.builder.errors.minLength", { n: min }));
    }
    const regex = /^[a-zA-Z0-9_ ]+$/;
    if (!regex.test(name)) {
        errors.push(i18nKey("bots.builder.errors.alphaOnly"));
    }
    return errors;
}
function validatePrincipal(p, mode) {
    if (mode === "update" && p === "")
        return true;
    try {
        Principal.fromText(p);
        return true;
    }
    catch (_) {
        return false;
    }
}
export function validEndpoint(endpoint) {
    return validOrigin(endpoint);
}
export function validateBot(principal, bot, mode) {
    const errors = new ValidationErrors();
    errors.addErrors(`bot_name`, validBotComponentName(bot.name, MIN_BOT_NAME_LENGTH));
    if (bot.ownerId === "") {
        errors.addErrors("bot_owner", i18nKey("bots.builder.errors.owner"));
    }
    if (!validEndpoint(bot.endpoint)) {
        errors.addErrors("bot_endpoint", i18nKey("bots.builder.errors.endpoint"));
    }
    if (!validatePrincipal(principal, mode)) {
        errors.addErrors("bot_principal", i18nKey("bots.builder.errors.principal"));
    }
    if (bot.definition.commands.length === 0 && bot.definition.autonomousConfig === undefined) {
        errors.addErrors("no_commands", i18nKey("bots.builder.errors.noCommands"));
    }
    if (containsDuplicateCommands(bot.definition.commands)) {
        errors.addErrors("duplicate_commands", i18nKey("bots.builder.errors.duplicateCommands"));
    }
    bot.definition.commands.forEach((command, i) => {
        if (!validateCommand(command, `command_${i}`, errors)) {
            errors.addErrors(`command_${i}`, i18nKey("Command has errors"));
        }
    });
    return errors;
}
function validateCommand(command, errorPath, errors) {
    let valid = true;
    const nameErrors = validBotComponentName(command.name, MIN_COMMAND_NAME_LENGTH);
    if (nameErrors.length > 0) {
        errors.addErrors(`${errorPath}_name`, nameErrors);
        valid = false;
    }
    if (containsDuplicateParams(command.params)) {
        errors.addErrors(`${errorPath}_duplicate_params`, i18nKey("bots.builder.errors.duplicateParams"));
    }
    command.params.forEach((p, i) => {
        const paramPath = `${errorPath}_param_${i}`;
        if (!validateParameter(p, paramPath, errors)) {
            errors.addErrors(paramPath, i18nKey("Parameter has errors"));
            valid = false;
        }
        // as the UserIndex checks them, which refuses the definition otherwise
        const earlier = command.params.slice(0, i);
        if (!conditionsOf(p).every((c) => conditionIsValid(c, earlier))) {
            const error = i18nKey("bots.builder.errors.condition", { param: p.name });
            errors.addErrors(paramPath, error);
            errors.addErrors(`${errorPath}_conditions`, error);
            valid = false;
        }
    });
    return valid;
}
// Whether a condition names an earlier string param with choices, and gives only its choices' values
function conditionIsValid(condition, earlier) {
    const param = earlier.find((p) => p.name === condition.param);
    return (param?.kind === "string" &&
        param.choices.length > 0 &&
        condition.values.length > 0 &&
        condition.values.every((v) => param.choices.some((c) => c.value === v)));
}
function containsDuplicateCommands(commands) {
    const set = new Set(commands.map((c) => c.name));
    return set.size < commands.length;
}
function containsDuplicateParams(params) {
    const set = new Set(params.map((p) => p.name));
    return set.size < params.length;
}
function validateParameter(param, errorPath, errors) {
    let valid = true;
    const nameErrors = validBotParamName(param.name, MIN_PARAM_NAME_LENGTH);
    if (nameErrors.length > 0) {
        errors.addErrors(`${errorPath}_name`, nameErrors);
        valid = false;
    }
    if (param.kind === "string") {
        param.choices.forEach((c, i) => {
            if (c.name.length < MIN_PARAM_NAME_LENGTH) {
                errors.addErrors(`${errorPath}_choices_${i}_name`, i18nKey("bots.builder.errors.minLength", { n: MIN_PARAM_NAME_LENGTH }));
                valid = false;
            }
            if (c.value.length < MIN_PARAM_NAME_LENGTH) {
                errors.addErrors(`${errorPath}_choices_${i}_value`, i18nKey("bots.builder.errors.minLength", { n: MIN_PARAM_NAME_LENGTH }));
                valid = false;
            }
        });
    }
    if (param.kind === "integer") {
        param.choices.forEach((c, i) => {
            if (c.name.length < MIN_PARAM_NAME_LENGTH) {
                errors.addErrors(`${errorPath}_choices_${i}_name`, i18nKey("bots.builder.errors.minLength", { n: MIN_PARAM_NAME_LENGTH }));
                valid = false;
            }
        });
    }
    if (param.kind === "decimal") {
        param.choices.forEach((c, i) => {
            if (c.name.length < MIN_PARAM_NAME_LENGTH) {
                errors.addErrors(`${errorPath}_choices_${i}_name`, i18nKey("bots.builder.errors.minLength", { n: MIN_PARAM_NAME_LENGTH }));
                valid = false;
            }
        });
    }
    return valid;
}
function validOrigin(origin) {
    if (!origin)
        return false;
    try {
        const o = new URL(origin);
        return o.origin === origin;
    }
    catch (_) {
        return false;
    }
}
export function botActionScopeFromExecutionContext(ctx) {
    switch (ctx.kind) {
        case "community":
            return {
                kind: "community_scope",
                communityId: ctx,
            };
        default:
            return {
                kind: "chat_scope",
                chatId: ctx,
                messageId: random64(),
                threadRootMessageIndex: undefined,
            };
    }
}
export function installationLocationsEqual(a, b) {
    if (a.kind !== b.kind)
        return false;
    switch (a.kind) {
        case "community":
            return communityIdentifiersEqual(a, b);
        default:
            return chatIdentifiersEqual(a, b);
    }
}
export function botActionScopeFromInstallLocation(location) {
    switch (location.kind) {
        case "community":
            return {
                kind: "community_scope",
                communityId: { kind: "community", communityId: location.communityId },
            };
        default:
            return {
                kind: "chat_scope",
                chatId: location,
                messageId: random64(),
                threadRootMessageIndex: undefined,
            };
    }
}
export function commandSupportsDirectMessages(command) {
    const [first, ...rest] = command.params;
    return command.directMessages && first?.kind === "string" && rest.every((p) => !p.required);
}
export function directMessageCommandInstance(bot, text) {
    const command = bot.definition.commands.find(commandSupportsDirectMessages);
    if (command) {
        return {
            kind: "external_bot",
            id: bot.id,
            endpoint: bot.endpoint,
            command: {
                name: command.name,
                arguments: shownArgs(command.params, createArgsFromSchema(command.params, [text])),
                placeholder: command.placeholder,
            },
        };
    }
}
export function emptyWebhookInstance() {
    return {
        id: "",
        name: "",
    };
}
